/**
 * `/liturgies/{id}/access` — who can reach this celebration.
 *
 * ## The thing this screen must not do
 *
 * **It cannot show you an existing link, and it says so.** Only the verifier is stored, so there is
 * genuinely nothing to reveal — and a screen that stayed quiet about that would leave somebody
 * hunting for a "show link" control that can never exist. The list is deliberately about *access*,
 * not about links: labels, kinds, whether each still works, when it was last used.
 *
 * A newly minted link is shown once, masked, with a deliberate reveal (the mockup's requirement).
 * Masking is not security theatre here — it is shoulder-protection in a parish office where a
 * screen faces the room.
 *
 * ## Three operations, three buttons
 *
 * Revoke · Regenerate · Replace short link are not variations of one another, and the screen refuses
 * to let them look like one control with options. Each states its consequence in the sentence next
 * to it, because "regenerate" and "replace the short link" sound interchangeable and are not: one
 * breaks every link in every inbox, the other breaks only what was printed.
 *
 * ## Two mechanisms, kept apart on purpose
 *
 * Everything above concerns **Capability Links** — bearer credentials. Below the grid sit the two
 * cards that concern the other mechanism: **delegations**, authority attached to an Account. They
 * are listed separately, revoked separately and shared separately because the server keeps them in
 * separate tables behind separate routes, and one merged list is how somebody revokes the wrong
 * one during the incident where it matters.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from '@tanstack/react-router';

import type { ApiError } from '../../api/client';
import { Notice } from '../../components/primitives';
import { WorkspaceTabs } from '../../components/composites/WorkspaceTabs';
import { useAuth } from '../auth/useAuth';
import { fetchHistory } from '../history/api';
import {
  fetchCollaborators,
  fetchLiturgy,
  fetchMyActions,
  revokeCollaborator,
  shareWithAccount,
  type Collaborator,
} from '../liturgies/api';
import { type DelegableAction, isDelegableAction, DELEGABLE_ACTIONS } from '@pius/contracts';
import {
  fetchCapabilities,
  issueCapability,
  KIND_COPY,
  regenerateCapability,
  replaceShortLink,
  revokeCapability,
  STATE_COPY,
  type CapabilityKind,
  type CapabilityRecord,
  type IssuedLink,
} from './api';
import './access.css';

export function AccessScreen(): React.JSX.Element {
  const { id } = useParams({ from: '/liturgies/$id/access' });
  const queryClient = useQueryClient();
  const { t } = useTranslation('common');

  const [issued, setIssued] = useState<IssuedLink | null>(null);

  const liturgy = useQuery({ queryKey: ['liturgy', id], queryFn: () => fetchLiturgy(id) });
  const grants = useQuery({
    queryKey: ['capabilities', id],
    queryFn: () => fetchCapabilities(id),
  });

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['capabilities', id] });
  };

  const error = grants.error instanceof Error ? (grants.error as ApiError) : undefined;

  return (
    <div className="acc">
      <header className="acc__head">
        <div>
          <h1 className="acc__title">{t('workspace.access')}</h1>
          <p className="acc__lede">
            {liturgy.data
              ? t('access.lede', { title: liturgy.data.title })
              : t('access.lede_fallback')}
          </p>
        </div>
        <Link className="acc__link" to="/liturgies/$id/history" params={{ id }}>
          {t('workspace.history')} →
        </Link>
      </header>

      <WorkspaceTabs liturgyId={id} />

      {error ? <Notice referenceId={error.correlationId}>{error.message}</Notice> : null}

      <div className="acc__grid">
        <div className="acc__main">
          {/* Takes the column while it exists. This link cannot be shown again, and nothing should
              compete with it for the reader's attention. */}
          {issued ? <IssuedPanel issued={issued} onDone={() => setIssued(null)} /> : null}

          <section className="acc__card">
            <p className="acc__eyebrow">{t('access.capabilities.eyebrow')}</p>

            {grants.isPending ? (
              <p className="acc__fine">{t('access.capabilities.loading')}</p>
            ) : grants.data?.data.length === 0 ? (
              <p className="acc__body">{t('access.capabilities.empty')}</p>
            ) : (
              <ul className="acc__list">
                {(grants.data?.data ?? []).map((grant) => (
                  <GrantRow key={grant.id} grant={grant} onChanged={refresh} onLink={setIssued} />
                ))}
              </ul>
            )}
          </section>

          <IssueForm
            liturgyId={id}
            onIssued={async (link) => {
              setIssued(link);
              await refresh();
            }}
          />
        </div>

        <aside className="acc__side">
          <section className="acc__card acc__card--warm">
            <p className="acc__eyebrow">{t('access.aside.why_no_link_heading')}</p>
            <p className="acc__body">{t('access.aside.why_no_link_body')}</p>
            <p className="acc__body acc__body--strong">{t('access.aside.why_no_link_strong')}</p>
            <p className="acc__fine">{t('access.aside.why_no_link_fine')}</p>
          </section>

          <section className="acc__card">
            <p className="acc__eyebrow">{t('access.aside.link_is_authority_heading')}</p>
            <p className="acc__body">{t('access.aside.link_is_authority_body')}</p>
            <p className="acc__fine">{t('access.aside.link_is_authority_fine')}</p>
          </section>
        </aside>
      </div>

      {/* The other mechanism. Below the grid rather than inside it, so nothing suggests a delegation
          is a kind of link. */}
      <div className="acc__pair">
        <DelegationsCard liturgyId={id} />
        <CapabilityHistoryCard liturgyId={id} />
      </div>
    </div>
  );
}

// ── a freshly minted link ───────────────────────────────────────────────────

function IssuedPanel({
  issued,
  onDone,
}: {
  issued: IssuedLink;
  onDone: () => void;
}): React.JSX.Element {
  const { t } = useTranslation('common');

  return (
    <section className="acc__card acc__card--warm">
      <p className="acc__eyebrow">{t('access.issued.copy_now')}</p>
      <p className="acc__body acc__body--strong">{t('access.issued.only_time')}</p>

      {issued.url ? <Revealable label={t('access.issued.share_label')} value={issued.url} /> : null}

      {issued.short_url ? (
        <>
          <Revealable label={t('access.issued.short_label')} value={issued.short_url} />
          <p className="acc__fine">{t('access.issued.short_help')}</p>
        </>
      ) : null}

      <div className="acc__actions">
        <button type="button" className="acc__btn" onClick={onDone}>
          {t('access.issued.copied')}
        </button>
      </div>
    </section>
  );
}

/**
 * Masked until asked for, per the mockup's "deliberate reveal".
 *
 * The copy button works **without** revealing, which is the point: in a parish office where a
 * monitor faces the room, the common case is copy-and-send, and that should not require putting a
 * live credential on screen at all.
 */
function Revealable({ label, value }: { label: string; value: string }): React.JSX.Element {
  const { t } = useTranslation('common');
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);

  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    } catch {
      // Clipboard access can be refused, and there is a perfectly good fallback: show it and let
      // them select it by hand. Failing silently would be the only bad outcome here.
      setRevealed(true);
    }
  };

  return (
    <div className="acc__reveal">
      <span className="acc__reveal-label">{label}</span>
      <code className={revealed ? 'acc__link-value' : 'acc__link-value acc__link-value--masked'}>
        {revealed ? value : mask(value)}
      </code>
      <div className="acc__reveal-actions">
        <button type="button" className="acc__btn acc__btn--small" onClick={copy}>
          {copied ? t('access.issued.copied_badge') : t('access.issued.copy')}
        </button>
        <button type="button" className="acc__ghost" onClick={() => setRevealed(!revealed)}>
          {revealed ? t('access.issued.hide') : t('access.issued.show')}
        </button>
      </div>
    </div>
  );
}

/** Origin and shape stay visible; the credential does not. */
function mask(url: string): string {
  const cut = url.lastIndexOf('/');
  return cut === -1 ? '••••••••' : `${url.slice(0, cut + 1)}${'•'.repeat(12)}`;
}

// ── one grant ───────────────────────────────────────────────────────────────

function GrantRow({
  grant,
  onChanged,
  onLink,
}: {
  grant: CapabilityRecord;
  onChanged: () => Promise<void>;
  onLink: (link: IssuedLink) => void;
}): React.JSX.Element {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const [confirmingRevoke, setConfirmingRevoke] = useState(false);

  const revoke = useMutation({
    mutationFn: () => revokeCapability(grant.id),
    onSuccess: async () => {
      setConfirmingRevoke(false);
      await onChanged();
    },
  });

  const regenerate = useMutation({
    mutationFn: () => regenerateCapability(grant.id),
    onSuccess: async (link) => {
      onLink(link);
      await onChanged();
    },
  });

  const replaceShort = useMutation({
    mutationFn: () => replaceShortLink(grant.id),
    onSuccess: async (link) => {
      onLink(link);
      await onChanged();
    },
  });

  const error = [revoke.error, regenerate.error, replaceShort.error].find(
    (e) => e instanceof Error,
  ) as ApiError | undefined;

  const busy = revoke.isPending || regenerate.isPending || replaceShort.isPending;

  const kindLabel = t(`access.kinds.${grant.kind}.label`, {
    defaultValue: KIND_COPY[grant.kind]?.label ?? grant.kind,
  });
  const kindNote = t(`access.kinds.${grant.kind}.note`, {
    defaultValue: KIND_COPY[grant.kind]?.note ?? '',
  });
  const stateLabel = t(`access.states.${grant.state}`, {
    defaultValue: STATE_COPY[grant.state] ?? grant.state,
  });

  return (
    <li className="acc__row">
      <div className="acc__row-head">
        <div className="acc__row-main">
          <div className="acc__row-name">
            {grant.label ?? kindLabel}
            {grant.label ? <span className="acc__row-kind"> · {kindLabel}</span> : null}
          </div>
          <div className="acc__row-meta">
            {grant.last_used_at
              ? t('access.capabilities.last_used', { date: grant.last_used_at.slice(0, 10) })
              : t('access.capabilities.never_used')}
            {grant.expires_at
              ? ` · ${t('access.capabilities.expires', { date: grant.expires_at.slice(0, 10) })}`
              : ''}
            {/* Anonymous issuance is normal, not a gap: a Liturgy created through an Administrator
                Link has no Account anywhere in its lineage. */}
            {grant.issued_by_account ? '' : ` · ${t('access.capabilities.shared_by_link')}`}
          </div>
        </div>

        <span className={`acc-pill acc-pill--${grant.state}`}>{stateLabel}</span>

        {grant.usable ? (
          <button type="button" className="acc__ghost" onClick={() => setOpen(!open)}>
            {open ? t('access.capabilities.close') : t('access.capabilities.manage')}
          </button>
        ) : (
          <span />
        )}
      </div>

      {open && grant.usable ? (
        <div className="acc__row-body">
          <p className="acc__fine">{kindNote}</p>

          {/* Three operations, three separate acts with their consequences stated. Presenting them
              as one control with a mode is how a "revoked" link keeps working. */}
          <div className="acc__op">
            <div>
              <div className="acc__op-name">{t('access.capabilities.replace_short')}</div>
              <p className="acc__fine">{t('access.capabilities.replace_short_help')}</p>
            </div>
            <button
              type="button"
              className="acc__btn acc__btn--quiet"
              disabled={busy}
              onClick={() => replaceShort.mutate()}
            >
              {t('access.capabilities.replace_short')}
            </button>
          </div>

          <div className="acc__op">
            <div>
              <div className="acc__op-name">{t('access.capabilities.regenerate')}</div>
              <p className="acc__fine">{t('access.capabilities.regenerate_help')}</p>
            </div>
            <button
              type="button"
              className="acc__btn acc__btn--quiet"
              disabled={busy}
              onClick={() => regenerate.mutate()}
            >
              {t('access.capabilities.regenerate')}
            </button>
          </div>

          <div className="acc__op acc__op--grave">
            <div>
              <div className="acc__op-name">{t('access.capabilities.revoke')}</div>
              <p className="acc__fine">{t('access.capabilities.revoke_help')}</p>
            </div>

            {confirmingRevoke ? (
              <div className="acc__confirm">
                <button
                  type="button"
                  className="acc__btn acc__btn--grave"
                  disabled={busy}
                  onClick={() => revoke.mutate()}
                >
                  {revoke.isPending
                    ? t('access.capabilities.revoking')
                    : t('access.capabilities.confirm_revoke_action')}
                </button>
                <button
                  type="button"
                  className="acc__ghost"
                  onClick={() => setConfirmingRevoke(false)}
                >
                  {t('access.capabilities.confirm_revoke_cancel')}
                </button>
              </div>
            ) : (
              <button
                type="button"
                className="acc__btn acc__btn--quiet"
                disabled={busy}
                onClick={() => setConfirmingRevoke(true)}
              >
                {t('access.capabilities.revoke')}
              </button>
            )}
          </div>

          {error ? <Notice referenceId={error.correlationId}>{error.message}</Notice> : null}
        </div>
      ) : null}
    </li>
  );
}

// ── issuing ─────────────────────────────────────────────────────────────────

function IssueForm({
  liturgyId,
  onIssued,
}: {
  liturgyId: string;
  onIssued: (link: IssuedLink) => Promise<void>;
}): React.JSX.Element {
  const { t } = useTranslation('common');
  const [kind, setKind] = useState<CapabilityKind>('participant');
  const [label, setLabel] = useState('');
  const [expiresAt, setExpiresAt] = useState('');

  const issue = useMutation({
    mutationFn: () =>
      issueCapability(liturgyId, {
        kind,
        label: label.trim() || null,
        expires_at: expiresAt || null,
      }),
    onSuccess: async (link) => {
      setLabel('');
      setExpiresAt('');
      await onIssued(link);
    },
  });

  const error = issue.error instanceof Error ? (issue.error as ApiError) : undefined;

  return (
    <section className="acc__card">
      <p className="acc__eyebrow">{t('access.share.heading')}</p>

      <div className="acc__kinds">
        {(['participant', 'administrator'] as const).map((value) => (
          <button
            key={value}
            type="button"
            className={`acc__kind${kind === value ? ' acc__kind--on' : ''}`}
            onClick={() => setKind(value)}
          >
            <span className="acc__kind-label">
              {t(`access.kinds.${value}.label`, { defaultValue: KIND_COPY[value]?.label })}
            </span>
            <span className="acc__kind-note">
              {t(`access.kinds.${value}.note`, { defaultValue: KIND_COPY[value]?.note })}
            </span>
          </button>
        ))}
      </div>

      <label className="acc__field">
        <span className="acc__field-label">{t('access.issue_form.who_label')}</span>
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder={t('access.issue_form.who_placeholder')}
        />
        {/* The only way to tell two links apart later, since neither can be displayed. */}
        <span className="acc__fine">{t('access.issue_form.who_help')}</span>
      </label>

      <label className="acc__field">
        <span className="acc__field-label">{t('access.issue_form.expires_label')}</span>
        <input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
        <span className="acc__fine">{t('access.issue_form.expires_help')}</span>
      </label>

      {error ? <Notice referenceId={error.correlationId}>{error.message}</Notice> : null}

      <div className="acc__actions">
        <button
          type="button"
          className="acc__btn"
          disabled={issue.isPending}
          onClick={() => issue.mutate()}
        >
          {issue.isPending ? t('access.share.submitting') : t('access.share.submit')}
        </button>
      </div>
    </section>
  );
}

// ── Account delegations ─────────────────────────────────────────────────────

/**
 * Who holds standing access as an Account, and the way to take it back.
 *
 * **The canvas draws a name and initials here. There is nothing to draw them from.**
 * `GET /liturgies/:id/collaborators` returns `account_id`, `scope` and `created_at`, because
 * planning has no port that resolves an Account to anything a human reads — Task 12 considered
 * opening one and deliberately did not, since that is a decision about a context boundary. So the
 * row shows the identifier it actually has, in the mono face that marks an identifier, and the card
 * says why rather than leaving a reader to wonder. Initials derived from a UUID would be an
 * invention, and a fabricated identity on an access screen is worse than an unfamiliar id.
 *
 * The list is not decorative: it is where somebody checks who can reach a celebration, so it is
 * refetched after every act that changes it.
 */
function DelegationsCard({ liturgyId }: { liturgyId: string }): React.JSX.Element {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const { account } = useAuth();
  const [sharing, setSharing] = useState(false);

  const delegations = useQuery({
    queryKey: ['collaborators', liturgyId],
    queryFn: () => fetchCollaborators(liturgyId),
  });

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['collaborators', liturgyId] });
  };

  const rows = delegations.data?.data ?? [];

  // **Both halves of what `share()` requires, asked before the button is drawn.**
  //
  // `manage_access` — evidenced by the list having been readable at all, since `list()` is gated on
  // exactly that — and an **Account**, because a delegation outlives the link that created it and
  // only a Persistent Administrator may write one (`requirePersistentAdministrator`). Drawing the
  // button for a requester missing either would offer an act that ends in a concealing 404, which
  // is the screen making a promise the domain will refuse to keep.
  //
  // An Administrator Capability bearer is the case worth naming: they may read this list and may
  // not add to it, so they see who holds access and are offered no way to grant it.
  const mayShare = delegations.isSuccess && account !== null;

  return (
    <section className="acc__card">
      <p className="acc__eyebrow">{t('access.delegations.eyebrow')}</p>

      {delegations.isPending ? (
        <p className="acc__body">{t('access.delegations.loading')}</p>
      ) : delegations.isError ? (
        <p className="acc__body">{t('access.delegations.error')}</p>
      ) : rows.length === 0 ? (
        <p className="acc__body">{t('access.delegations.empty')}</p>
      ) : (
        <>
          <ul className="acc__delegs">
            {rows.map((row) => (
              <DelegationRow
                key={row.account_id}
                liturgyId={liturgyId}
                delegation={row}
                onChanged={refresh}
              />
            ))}
          </ul>
          <p className="acc__fine">{t('access.delegations.identifier_note')}</p>
        </>
      )}

      {sharing && mayShare ? (
        <ShareForm liturgyId={liturgyId} onClose={() => setSharing(false)} onShared={refresh} />
      ) : null}

      {/* **Placement inferred.** The canvas gives every delegation row a Revoke and draws no way to
          add one, while Capabilities get "Create participant link" — the entry point simply is not
          drawn. This mirrors the capability footer: primary button beside the fine print, and the
          statement about Ownership stays the last thing on the card either way. */}
      <div className="acc__foot">
        {mayShare && !sharing ? (
          <button type="button" className="acc__btn" onClick={() => setSharing(true)}>
            {t('access.share.open')}
          </button>
        ) : null}
        <p className="acc__fine acc__fine--foot">{t('access.delegations.footer')}</p>
      </div>
    </section>
  );
}

/** One Account's standing access, with the identifier it has rather than a name it does not. */
function DelegationRow({
  liturgyId,
  delegation,
  onChanged,
}: {
  liturgyId: string;
  delegation: Collaborator;
  onChanged: () => Promise<void>;
}): React.JSX.Element {
  const { t, i18n } = useTranslation('common');
  const [confirming, setConfirming] = useState(false);

  const revoke = useMutation({
    mutationFn: () => revokeCollaborator(liturgyId, delegation.account_id),
    onSuccess: async () => {
      setConfirming(false);
      await onChanged();
    },
  });

  const error = revoke.error instanceof Error ? (revoke.error as ApiError) : undefined;

  // Anything the client does not recognise is shown as it arrived rather than dropped: an
  // unfamiliar action is still authority this Account holds, and hiding it would understate the
  // access in force.
  const scope = delegation.scope.map((action) =>
    isDelegableAction(action) ? t(`access.actions.${action}.label`) : action,
  );

  return (
    <li className="acc__deleg">
      <div className="acc__deleg-head">
        <div className="acc__deleg-main">
          <span className="acc__deleg-label">{t('access.delegations.account_label')}</span>
          <code className="acc__deleg-id">{delegation.account_id}</code>
          <div className="acc__deleg-scope">
            {t('access.delegations.scope', { actions: scope.join(' · ') })}
          </div>
          <div className="acc__deleg-since">
            {t('access.delegations.since', {
              date: formatDay(delegation.created_at, i18n.language),
            })}
          </div>
        </div>

        {confirming ? null : (
          <button
            type="button"
            className="acc__btn acc__btn--quiet acc__btn--small"
            onClick={() => setConfirming(true)}
          >
            {t('access.delegations.revoke')}
          </button>
        )}
      </div>

      {/* Destructive, so it states what happens rather than asking whether they are sure. */}
      {confirming ? (
        <div className="acc__deleg-confirm">
          <p className="acc__fine">{t('access.delegations.confirm')}</p>
          <div className="acc__confirm">
            <button
              type="button"
              className="acc__btn acc__btn--grave acc__btn--small"
              disabled={revoke.isPending}
              onClick={() => revoke.mutate()}
            >
              {revoke.isPending
                ? t('access.delegations.revoking')
                : t('access.delegations.confirm_action')}
            </button>
            <button type="button" className="acc__ghost" onClick={() => setConfirming(false)}>
              {t('access.delegations.confirm_cancel')}
            </button>
          </div>
        </div>
      ) : null}

      {error ? <Notice referenceId={error.correlationId}>{error.message}</Notice> : null}
    </li>
  );
}

/**
 * Share with an Account — an address and a scope.
 *
 * ## The confirmation is the same in both cases, on purpose
 *
 * `POST .../collaborators` answers `202` with a constant body whether or not the address belongs to
 * an Account: `CollaboratorService` treats that difference as something the caller must not learn,
 * and a `201` or a body reporting "delegated" versus "invited" would turn the route into an
 * account-existence oracle. So there is **one** confirmation here, with the same words, the same
 * weight and the same timing for both outcomes. Rebuilding the distinction in the browser — a
 * different icon, a different sentence, a pause before one of them — would hand back exactly what
 * the API refuses to expose.
 *
 * The list above is refetched either way, which is the residual `ShareOutcome` already documents:
 * an administrator can infer registration by sharing and then reading the delegation list. That is
 * inherent to "share with an existing Account" and is not closed here. Refusing to refresh would
 * not close it either — it would only leave the access list stale, and a stale list of who can
 * reach a celebration is the more serious harm.
 *
 * ## The scope control offers only what the sharer holds
 *
 * `share()` intersects with the sharer's own authority server-side and stores the intersection, so
 * offering more would be *safe* and *dishonest*: a ticked box the server silently drops teaches the
 * reader that the screen does not mean what it says. `GET /liturgies/:id/my-actions` is what makes
 * the honest version possible, narrowed again by `DELEGABLE_ACTIONS` so `transfer_ownership` — held
 * by every Owner, conveyed by no delegation — never appears.
 */
function ShareForm({
  liturgyId,
  onClose,
  onShared,
}: {
  liturgyId: string;
  onClose: () => void;
  onShared: () => Promise<void>;
}): React.JSX.Element {
  const { t } = useTranslation('common');

  const [email, setEmail] = useState('');
  const [scope, setScope] = useState<DelegableAction[]>(['view']);
  const [sentTo, setSentTo] = useState<string | null>(null);

  const mine = useQuery({
    queryKey: ['my-actions', liturgyId],
    queryFn: () => fetchMyActions(liturgyId),
  });

  // Two bounds, both narrowing. What the sharer holds, and what may be delegated at all.
  const offerable = DELEGABLE_ACTIONS.filter((action) => (mine.data?.data ?? []).includes(action));

  const share = useMutation({
    mutationFn: () => shareWithAccount(liturgyId, { email: email.trim(), scope }),
    onSuccess: async () => {
      setSentTo(email.trim());
      setEmail('');
      setScope(['view']);
      await onShared();
    },
  });

  const error = share.error instanceof Error ? (share.error as ApiError) : undefined;

  if (sentTo) {
    return (
      <div className="acc__share acc__share--sent">
        <p className="acc__body acc__body--strong">
          {t('access.share.sent_heading', { email: sentTo })}
        </p>
        <p className="acc__fine">{t('access.share.sent_body')}</p>
        <div className="acc__actions">
          <button
            type="button"
            className="acc__btn acc__btn--quiet"
            onClick={() => {
              setSentTo(null);
              onClose();
            }}
          >
            {t('access.share.sent_dismiss')}
          </button>
        </div>
      </div>
    );
  }

  const toggle = (action: DelegableAction): void => {
    setScope((current) =>
      current.includes(action) ? current.filter((a) => a !== action) : [...current, action],
    );
  };

  return (
    <div className="acc__share">
      <p className="acc__deleg-label">{t('access.share.heading')}</p>
      <p className="acc__fine">{t('access.share.note')}</p>

      <label className="acc__field">
        <span className="acc__field-label">{t('access.share.email_label')}</span>
        <input
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <span className="acc__fine">{t('access.share.email_help')}</span>
      </label>

      <fieldset className="acc__scope">
        <legend className="acc__field-label">{t('access.share.scope_label')}</legend>

        {mine.isPending ? (
          <p className="acc__fine">{t('access.share.scope_loading')}</p>
        ) : mine.isError ? (
          <p className="acc__fine">{t('access.share.scope_error')}</p>
        ) : offerable.length === 0 ? (
          <p className="acc__fine">{t('access.share.scope_none')}</p>
        ) : (
          <>
            {/* Before the list, not after it. It is the sentence that explains why the list is as
                short as it is, and after ten rows nobody reads it. */}
            <p className="acc__fine acc__fine--lead">{t('access.share.scope_help')}</p>
            {offerable.map((action) => (
              <label key={action} className="acc__scope-item">
                <input
                  type="checkbox"
                  checked={scope.includes(action)}
                  onChange={() => toggle(action)}
                />
                <span>
                  <span className="acc__scope-label">{t(`access.actions.${action}.label`)}</span>
                  <span className="acc__scope-note">{t(`access.actions.${action}.note`)}</span>
                </span>
              </label>
            ))}
          </>
        )}
      </fieldset>

      {error ? <Notice referenceId={error.correlationId}>{error.message}</Notice> : null}

      <div className="acc__actions">
        <button
          type="button"
          className="acc__btn"
          disabled={share.isPending || email.trim() === '' || scope.length === 0}
          onClick={() => share.mutate()}
        >
          {share.isPending ? t('access.share.submitting') : t('access.share.submit')}
        </button>
        <button type="button" className="acc__ghost" onClick={onClose}>
          {t('access.share.cancel')}
        </button>
      </div>
    </div>
  );
}

// ── capability history ──────────────────────────────────────────────────────

/**
 * The lifecycle of each grant — and never the grant itself.
 *
 * There is a real source: `GET /liturgies/:id/history` carries a `capability` record type built
 * from `capability_grants`, so this card is not inventing one. What that source contains is the
 * kind, the operator's own label, the dates and the state; the raw credential is not stored
 * anywhere, so there is structurally nothing here that *could* be a token or a link. The card still
 * renders only the timestamp and that text — never `entry.id`, which is the grant's identifier and
 * has no business on screen.
 *
 * `capability` arrives as **null, not `[]`** for a requester who may not read it, because "there is
 * nothing" and "this is not yours to see" are different facts. Both are handled, separately.
 */
function CapabilityHistoryCard({ liturgyId }: { liturgyId: string }): React.JSX.Element {
  const { t, i18n } = useTranslation('common');

  const history = useQuery({
    queryKey: ['history', liturgyId],
    queryFn: () => fetchHistory(liturgyId),
  });

  const entries = history.data?.data.capability;

  return (
    <section className="acc__card acc__card--warm">
      <p className="acc__eyebrow">{t('access.history.eyebrow')}</p>

      {history.isPending ? (
        <p className="acc__body">{t('access.history.loading')}</p>
      ) : history.isError || entries === null || entries === undefined ? (
        <p className="acc__body">{t('access.history.withheld')}</p>
      ) : entries.length === 0 ? (
        <p className="acc__body">{t('access.history.empty')}</p>
      ) : (
        <ul className="acc__hist">
          {entries.map((entry) => (
            <li key={entry.id} className="acc__hist-item">
              <time className="acc__hist-at" dateTime={entry.at}>
                {formatDay(entry.at, i18n.language)}
              </time>
              <span className="acc__hist-text">
                <span>{entry.summary}</span>
                {entry.after ? (
                  <span className="acc__hist-state">{historyState(entry.after, t)}</span>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      )}

      <p className="acc__fine acc__fine--foot acc__fine--alone">{t('access.history.footer')}</p>
    </section>
  );
}

/**
 * The four terminal words `history.controller.ts` writes into `after`.
 *
 * Anything else falls through unchanged rather than being hidden: an unlabelled record is still a
 * record, and a state this build does not recognise is exactly the case where guessing is worst.
 */
function historyState(after: string, t: (key: string) => string): string {
  const known = ['active', 'revoked', 'expired', 'replaced'];
  return known.includes(after) ? t(`access.history.state.${after}`) : after;
}

/** A day, in the language the interface is being read in — not the browser's default. */
function formatDay(iso: string, language: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso.slice(0, 10);

  return parsed.toLocaleDateString(language, { day: 'numeric', month: 'short', year: 'numeric' });
}
